import React, { useState, useEffect, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import {
  fetchBanks,
  validateReportPrerequisites,
  indicizzaReport,
  checkSources,
  resolveSource,
} from "../../api/functionApi";
import { findPendingSourceDecisions, PendingSourceDecision } from "../../api/beSourceDecisions";
import { Bank, ValidationResultReport, SourceResolveDecision } from "../../api/apiTypes";
import { ApiError } from "../../api/wrappers";
import { Stepper } from "../Stepper";
import { shouldKeepPolling, unknownLabels, hasLabel } from "../validationLabels";
import { defaultReferenceYear } from "../referenceYear";
import { logAzioneBe } from "../apiLog";
import Step1SelectBanks from "./newReport/Step1SelectBanks";
import Step2Validation from "./newReport/Step2Validation";
import Step3ReportDetails from "./newReport/Step3ReportDetails";

interface Props {
  onConferma: (
    nome: string,
    descrizione: string,
    bankNames: string[],
    referenceYear: number,
  ) => Promise<void>;
  onAnnulla: () => void;
}

/** Ogni quanto ripetere /validate mentre qualcosa sta girando lato server. */
const POLL_INTERVAL_MS = 5000;

/**
 * Banche minime per creare un report: sotto questa soglia il server risponde 400.
 * Usata anche nel messaggio d'errore, cosi' controllo e testo non divergono.
 */
const MIN_BANCHE = 16;

/**
 * Esito di una decisione su una fonte cambiata, mostrato dentro il banner.
 *
 * Volutamente NON un alert: il wizard è a sua volta un popup SweetAlert, e una
 * `Swal.fire` annidata ne sostituisce il contenuto invece di sovrapporsi.
 */
export interface EsitoAzione {
  /** Il bottone premuto. */
  titolo: string;
  testo: string;
  /** Presente solo quando il server non ha fatto quello che ci si aspettava. */
  avviso?: string;
}

export interface EsitoResolve {
  tono: "ok" | "info" | "errore";
  messaggio: string;
  /** Classi da reindicizzare dopo uno switch: se valorizzate si offre il bottone. */
  daIndicizzare: string[];
}

const DialogNewReportForm: React.FC<Props> = ({ onConferma, onAnnulla }) => {
  const [step, setStep] = useState(1);
  const [nome, setNome] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [referenceYear, setReferenceYear] = useState<number>(defaultReferenceYear());
  const [banks, setBanks] = useState<Bank[]>([]);
  const [selectedBanks, setSelectedBanks] = useState<string[]>(() => {
    const saved = localStorage.getItem("selectedBanks");
    return saved ? JSON.parse(saved) : [];
  });
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResultReport | null>(null);
  const [expandedBanks, setExpandedBanks] = useState<Record<string, boolean>>({});
  const [step1Error, setStep1Error] = useState<string>("");
  const [loadingIndexing, setLoadingIndexing] = useState(false);
  const [loadingCheck, setLoadingCheck] = useState(false);
  /**
   * Vero subito dopo un /ingest che ha davvero avviato delle pipeline.
   * Serve a coprire la finestra fra la risposta di /ingest e il momento in cui
   * /validate comincia a segnalare `indexing_in_progress`: senza, per qualche
   * secondo si rivedrebbe "è necessario indicizzare" mentre l'indicizzazione gira.
   */
  const [avvioRecente, setAvvioRecente] = useState(false);
  const [pendingDecisions, setPendingDecisions] = useState<PendingSourceDecision[]>([]);
  const [loadingDecisions, setLoadingDecisions] = useState(false);
  const [resolvingSourceId, setResolvingSourceId] = useState<string | null>(null);
  const [esitoResolve, setEsitoResolve] = useState<EsitoResolve | null>(null);
  const [creating, setCreating] = useState(false);
  /** Esito della sola ultima azione: uno solo, per non impilare riepiloghi vecchi. */
  const [esitoAzione, setEsitoAzione] = useState<EsitoAzione | null>(null);
  /** Errore mostrato dentro il wizard: sul 409 accompagna il ritorno allo step 2. */
  const [erroreWizard, setErroreWizard] = useState("");

  // Evita che un polling in volo scriva su un dialog gia' chiuso.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const steps = [
    { id: 1, label: "Seleziona Banche" },
    { id: 2, label: "Validazione" },
    { id: 3, label: "Dettagli Report" },
  ];

  const selectedBankNames = banks
    .filter((b) => selectedBanks.includes(b.id))
    .map((b) => b.name);

  // --- Fetch banche ---
  useEffect(() => {
    const loadBanks = async () => {
      setLoadingBanks(true);
      try {
        const result = await fetchBanks();
        setBanks(result);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingBanks(false);
      }
    };
    loadBanks();
  }, []);

  /** Chiama /validate e normalizza la risposta (banche come chiavi di primo livello). */
  const runValidation = useCallback(async (bankIds: string[]): Promise<ValidationResultReport> => {
    const result = await validateReportPrerequisites(bankIds);
    logAzioneBe({
      azione: "Validazione prerequisiti",
      metodo: "POST",
      endpoint: "/api/v1/reports/best_execution/validate",
      payload: { bank_ids: bankIds },
      risposta: result,
      nota: "Sola lettura. Dice lo stato di ogni classe documento. Non scarica e non indicizza nulla.",
    });
    const formatted: ValidationResultReport = {
      status: result.status,
      general: result.general,
      banks: Object.fromEntries(
        Object.entries(result).filter(([key]) => key !== "status" && key !== "general"),
      ),
    };

    // Una label nuova lato BE non deve passare inosservata.
    const unknown = unknownLabels(formatted);
    if (unknown.length > 0) {
      console.warn("Label di /validate non riconosciute dal frontend:", unknown);
    }

    if (mounted.current) setValidationResult(formatted);
    return formatted;
  }, []);

  const handleCheckboxChange = (bankId: string) => {
    setSelectedBanks((prev) => {
      const newSelection = prev.includes(bankId)
        ? prev.filter((id) => id !== bankId)
        : [...prev, bankId];
      localStorage.setItem("selectedBanks", JSON.stringify(newSelection));
      return newSelection;
    });
  };

  const handleNextStep = async () => {
    if (step === 1) {
      if (selectedBanks.length < MIN_BANCHE) {
        setStep1Error(`Seleziona minimo ${MIN_BANCHE} banche per poter andare avanti.`);
        return;
      }
      setStep1Error("");

      setLoadingValidation(true);
      try {
        await runValidation(selectedBanks);
        setStep(2);
      } catch (err) {
        console.error(err);
        setErroreWizard(
          err instanceof Error ? err.message : "Errore nella validazione, riprova più tardi.",
        );
      } finally {
        setLoadingValidation(false);
      }
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handlePrevStep = () => setStep(step - 1);

  /**
   * La conferma vive dentro lo step 3, non in un popup: il wizard e' a sua volta
   * un popup SweetAlert e una `Swal.fire` annidata ne sostituirebbe il contenuto,
   * smontandolo. Serve che resti in piedi per poter tornare alla readiness sul 409.
   */
  const handleSubmit = async () => {
    setCreating(true);
    setErroreWizard("");
    try {
      await onConferma(nome.trim(), descrizione.trim(), selectedBankNames, referenceYear);
      Swal.close();
    } catch (err) {
      console.error(err);
      const messaggio = err instanceof Error ? err.message : "Riprova più tardi.";

      // 409: le fonti non sono pronte. Il documento chiede di mostrare il
      // `detail` e riportare alla schermata di readiness, dove si vede quale
      // banca e quale classe stanno bloccando.
      if (err instanceof ApiError && err.status === 409) {
        if (mounted.current) {
          setErroreWizard(messaggio);
          setStep(2);
        }
        await runValidation(selectedBanks).catch((e) =>
          console.error("Rivalidazione dopo il 409 fallita:", e),
        );
        return;
      }

      if (mounted.current) setErroreWizard(messaggio);
    } finally {
      if (mounted.current) setCreating(false);
    }
  };

  /** POST /ingest. Con `force` rilancia anche le unità già indicizzate. */
  const handleIndicizzaFonti = async (force = false) => {
    setLoadingIndexing(true);
    setAvvioRecente(false);
    setEsitoAzione(null);
    setErroreWizard("");
    try {
      const res = await indicizzaReport(selectedBankNames, force);
      logAzioneBe({
        azione: force ? 'Bottone "Riprocessa tutto"' : 'Bottone "Indicizza fonti"',
        metodo: "POST",
        endpoint: "/api/v1/reports/best_execution/ingest",
        payload: { bank_names: selectedBankNames, force },
        risposta: res,
        nota: force
          ? "Stesso endpoint di 'Indicizza fonti' ma force:true — rilancia anche le unità già indicizzate con successo e chiude le run rimaste appese. Non scarica niente dalle origini."
          : "Avvia le pipeline di indicizzazione sulle unità non ancora indicizzate. Salta quelle già riuscite. Non scarica niente dalle origini: lavora sui bytes già salvati.",
      });

      const parts = [
        `${res.triggered_pipelines?.length ?? 0} unità avviate su ${res.total_sources} trovate`,
      ];
      if (res.skipped_sources) parts.push(`${res.skipped_sources} saltate`);
      if (res.skipped_in_flight) parts.push(`${res.skipped_in_flight} già in corso`);
      if (res.skipped_no_snapshot) {
        parts.push(`${res.skipped_no_snapshot} senza bytes salvati (serve il backfill lato operatore)`);
      }
      if (res.abandoned_stale) parts.push(`${res.abandoned_stale} run perse rilanciate`);
      // `message` è il riepilogo scritto dal server: spesso spiega uno scarto
      // che i contatori da soli non giustificano.
      // Unità trovate, non saltate, ma nessuna pipeline avviata: l'indicizzazione
      // non è partita e non dipende dalle fonti. Senza questo avviso l'utente
      // vede solo "0 unità avviate" e continua a premere il bottone.
      const triggered = res.triggered_pipelines?.length ?? 0;
      const attese = Math.max(res.total_sources - res.skipped_sources, 0);
      const avviso =
        triggered === 0 && attese > 0
          ? `Nessuna pipeline è stata avviata, pur essendoci ${attese} unità da indicizzare. ` +
            `Non dipende dalle fonti né da qualcosa che puoi fare da qui: va verificata la ` +
            `configurazione di indicizzazione del backend. Risposta del server: "${res.message}".`
          : undefined;

      if (avviso) console.warn(`[BE] ${avviso}`);
      if (mounted.current) {
        setEsitoAzione({
          titolo: force ? "Riprocessa tutto" : "Indicizzazione",
          testo: parts.join(" · "),
          avviso,
        });
      }
      // Solo se il server dice di aver avviato qualcosa: non si annuncia
      // un'indicizzazione in corso quando non è partito niente.
      if (mounted.current) setAvvioRecente(triggered > 0);

      await runValidation(selectedBanks);
    } catch (err) {
      console.error(err);
      if (mounted.current) {
        setErroreWizard(
          err instanceof Error ? err.message : "Errore durante l'indicizzazione, riprova più tardi.",
        );
      }
    } finally {
      if (mounted.current) setLoadingIndexing(false);
    }
  };

  /**
   * POST /check-sources. Ogni fetch è un download vero, quindi è un'azione
   * esplicita: non parte al caricamento né dentro il polling.
   */
  const handleCheckSources = async () => {
    setLoadingCheck(true);
    setEsitoAzione(null);
    setErroreWizard("");
    try {
      const res = await checkSources(selectedBankNames);
      logAzioneBe({
        azione: 'Bottone "Controlla fonti"',
        metodo: "POST",
        endpoint: "/api/v1/reports/best_execution/check-sources",
        payload: { bank_names: selectedBankNames },
        risposta: res,
        nota: "Endpoint diverso dagli altri due. Ri-scarica davvero le origini remote e confronta i bytes con quelli registrati. Non indicizza niente: segnala solo se una fonte è cambiata.",
      });

      const parts = [`${res.queued.length} fonti in controllo su ${res.total_sources}`];
      if (res.skipped_pinned?.length) parts.push(`${res.skipped_pinned.length} file caricati a mano`);
      if (res.skipped_within_ttl?.length) {
        parts.push(`${res.skipped_within_ttl.length} HTML già controllate di recente`);
      }
      if (res.skipped_in_flight?.length) parts.push(`${res.skipped_in_flight.length} già in corso`);
      if (mounted.current) {
        setEsitoAzione({ titolo: "Controllo fonti", testo: parts.join(" · ") });
      }

      await runValidation(selectedBanks);
    } catch (err) {
      console.error(err);
      if (mounted.current) {
        setErroreWizard(
          err instanceof Error
            ? err.message
            : "Errore durante il controllo delle fonti, riprova più tardi.",
        );
      }
    } finally {
      if (mounted.current) setLoadingCheck(false);
    }
  };

  /** `keep`: tiene la versione registrata. `switch`: registra i bytes correnti e sposta i link. */
  const handleResolve = async (
    decision: SourceResolveDecision,
    pending: PendingSourceDecision,
    newUri?: string,
  ) => {
    setResolvingSourceId(pending.source.id);
    setEsitoResolve(null);
    try {
      const res = await resolveSource(pending.source.id, decision, newUri);
      logAzioneBe({
        azione: decision === "keep" ? 'Bottone "Tieni la versione attuale"' : 'Bottone "Passa alla nuova versione"',
        metodo: "POST",
        endpoint: `/api/v1/reports/best_execution/sources/${pending.source.id}/resolve`,
        payload: { decision, uri: newUri },
        risposta: res,
        nota:
          decision === "keep"
            ? "Tiene la versione registrata e prende per buono l'hash attuale dell'origine: la segnalazione torna solo se l'origine cambia di nuovo."
            : "Registra i bytes correnti come nuova fonte che sostituisce la vecchia, e sposta tutti i link attivi — su tutte le banche che condividono quella fonte. Dopo serve reindicizzare.",
      });

      if (mounted.current) {
        if (decision === "keep") {
          setEsitoResolve({
            tono: "ok",
            messaggio:
              "Versione attuale confermata. La segnalazione tornerà solo se l'origine cambia di nuovo.",
            daIndicizzare: [],
          });
        } else if (!res.new_source) {
          setEsitoResolve({
            tono: "info",
            messaggio:
              "All'origine ci sono di nuovo i bytes già registrati: non è stato creato nulla.",
            daIndicizzare: [],
          });
        } else {
          const classi = (res.needs_ingestion ?? []).map(
            (c) => `${c.bank_name ?? "general"}/${c.document_type}`,
          );
          setEsitoResolve({
            tono: "ok",
            messaggio:
              `Nuova versione registrata, ${res.moved_links ?? 0} collegamenti spostati.` +
              (classi.length ? " La nuova versione va ora indicizzata." : ""),
            daIndicizzare: classi,
          });
        }
      }

      await runValidation(selectedBanks);
    } catch (err) {
      console.error(err);
      if (mounted.current) {
        setEsitoResolve({
          tono: "errore",
          messaggio: err instanceof Error ? err.message : "Riprova più tardi.",
          daIndicizzare: [],
        });
      }
    } finally {
      if (mounted.current) setResolvingSourceId(null);
    }
  };

  // --- Polling di /validate mentre qualcosa gira lato server -----------------
  useEffect(() => {
    if (step !== 2 || !validationResult) return;
    if (!shouldKeepPolling(validationResult) && !avvioRecente) return;

    const timer = setTimeout(() => {
      runValidation(selectedBanks).catch((err) =>
        console.error("Polling di /validate fallito:", err),
      );
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [step, validationResult, selectedBanks, runValidation, avvioRecente]);

  // Chiude la finestra di avvio: o /validate conferma che sta girando (e da lì
  // in poi comanda il polling vero), oppure dopo un minuto si smette di
  // affermare che è in corso qualcosa di cui non si ha conferma.
  useEffect(() => {
    if (!avvioRecente) return;
    if (validationResult && hasLabel(validationResult, "indexing_in_progress")) {
      setAvvioRecente(false);
      return;
    }
    const timer = setTimeout(() => setAvvioRecente(false), 60000);
    return () => clearTimeout(timer);
  }, [avvioRecente, validationResult]);

  // --- Risale alle fonti da risolvere quando compare `source_changed` --------
  useEffect(() => {
    if (step !== 2 || !validationResult) return;

    if (!hasLabel(validationResult, "source_changed")) {
      setPendingDecisions([]);
      return;
    }

    let cancelled = false;
    setLoadingDecisions(true);
    findPendingSourceDecisions(validationResult, banks)
      .then((decisions) => {
        if (!cancelled && mounted.current) setPendingDecisions(decisions);
      })
      .catch((err) => console.error("Fonti da risolvere non recuperabili:", err))
      .finally(() => {
        if (!cancelled && mounted.current) setLoadingDecisions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [step, validationResult, banks]);

  const toggleBankAccordion = (bankName: string) => {
    setExpandedBanks((prev) => ({
      ...prev,
      [bankName]: !prev[bankName],
    }));
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <Step1SelectBanks
            banks={banks}
            selectedBanks={selectedBanks}
            loadingBanks={loadingBanks}
            step1Error={step1Error}
            onCheckboxChange={handleCheckboxChange}
            clearError={() => setStep1Error("")}
          />
        );
      case 2:
        if (!validationResult) return null;
        return (
          <Step2Validation
            validationResult={validationResult}
            expandedBanks={expandedBanks}
            toggleBankAccordion={toggleBankAccordion}
            handleIndicizzaFonti={handleIndicizzaFonti}
            handleCheckSources={handleCheckSources}
            handleResolve={handleResolve}
            handleNextStep={handleNextStep}
            handlePrevStep={handlePrevStep}
            loadingIndexing={loadingIndexing}
            loadingCheck={loadingCheck}
            loadingDecisions={loadingDecisions}
            resolvingSourceId={resolvingSourceId}
            pendingDecisions={pendingDecisions}
            avvioRecente={avvioRecente}
            esitoResolve={esitoResolve}
            esitoAzione={esitoAzione}
            erroreWizard={erroreWizard}
          />
        );
      case 3:
        return (
          <Step3ReportDetails
            nome={nome}
            descrizione={descrizione}
            referenceYear={referenceYear}
            setNome={setNome}
            setDescrizione={setDescrizione}
            setReferenceYear={setReferenceYear}
            onPrevStep={handlePrevStep}
            onSubmit={handleSubmit}
            creating={creating}
            errore={erroreWizard}
          />
        );
      default:
        return null;
    }
  };

  const renderStepButtons = () => {
    if (step === 1) {
      return (
        <div className="flex justify-end mt-4 gap-2">
          <button
            onClick={() => {
              onAnnulla?.();
              Swal.close();
            }}
            className="bg-gray-300 text-gray-800  hover:bg-gray-400 px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed "
          >
            Annulla
          </button>
          <button
            onClick={handleNextStep}
            className="bg-textPrimary text-white hover:bg-textSecondary px-4 py-2 rounded-lg text-sm font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loadingValidation}
          >
            {loadingValidation ? "Validating..." : "Avanti"}
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-4 w-full max-w-[80vw] max-h-[90vh] bg-white rounded shadow-lg overflow-hidden">
      <Stepper steps={steps} currentStep={step} />
      <h2 className="text-xl font-semibold mb-6 text-center">Crea Nuovo Report</h2>
      {renderStepContent()}
      {renderStepButtons()}
    </div>
  );
};

export default DialogNewReportForm;
